import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Minus, Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';

const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, removeFromCart, updateQuantity, cartTotal } = useCart();
  const navigate = useNavigate();

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setCartOpen(false)}
        className={`fixed inset-0 bg-neutral-900/30 z-[70] transition-opacity duration-300 ${
          cartOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-white z-[80] shadow-2xl transform transition-transform duration-500 ease-out flex flex-col ${
          cartOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 h-20 border-b border-neutral-200">
          <h3 className="text-[11px] tracking-[0.22em] uppercase">Shopping Bag</h3>
          <button onClick={() => setCartOpen(false)} aria-label="Close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-6 text-center">
              <p className="font-display text-2xl mb-3">Your bag is empty</p>
              <p className="text-sm text-neutral-500 mb-8">
                Discover our curated selection of timeless pieces.
              </p>
              <Link
                to="/shop"
                onClick={() => setCartOpen(false)}
                className="inline-block bg-neutral-900 text-white px-10 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-700 transition-colors"
              >
                Shop Now
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-neutral-200">
              {cart.map((item) => {
                const key = `${item.id}-${item.size}-${item.color}`;
                return (
                  <li key={key} className="flex gap-4 p-6">
                    <div className="w-24 h-32 bg-neutral-100 flex-shrink-0 overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 flex flex-col">
                      <div className="flex justify-between gap-2">
                        <p className="text-sm font-medium">{item.name}</p>
                        <button
                          onClick={() => removeFromCart(key)}
                          className="text-neutral-400 hover:text-neutral-900 transition-colors"
                          aria-label="Remove"
                        >
                          <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                      <p className="text-xs text-neutral-500 mt-1">
                        {item.color} · {item.size}
                      </p>
                      <div className="mt-auto flex items-center justify-between">
                        <div className="flex items-center border border-neutral-300">
                          <button
                            onClick={() => updateQuantity(key, item.quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100 transition-colors"
                            aria-label="Decrease"
                          >
                            <Minus className="w-3 h-3" strokeWidth={1.5} />
                          </button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(key, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100 transition-colors"
                            aria-label="Increase"
                          >
                            <Plus className="w-3 h-3" strokeWidth={1.5} />
                          </button>
                        </div>
                        <p className="text-sm">${item.price * item.quantity}</p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-neutral-200 p-6 space-y-5">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-500">Subtotal</span>
              <span className="font-medium">${cartTotal}</span>
            </div>
            <p className="text-xs text-neutral-500">
              Shipping and taxes calculated at checkout.
            </p>
            <button
              onClick={() => {
                setCartOpen(false);
                navigate('/checkout');
              }}
              className="w-full bg-neutral-900 text-white py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-700 transition-colors"
            >
              Checkout
            </button>
            <button
              onClick={() => setCartOpen(false)}
              className="w-full text-[11px] tracking-[0.22em] uppercase border-b border-neutral-900 inline-block pb-1 mx-auto"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
